import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import firebaseApp from "./Firecontact";

const DataContext = createContext();

export const useDataContext = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error("useDataContext must be used within a DataProvider");
    }
    return context;
};

// Page sizes for pagination
const PRODUCTS_PAGE_SIZE = 10;
const BLOGS_PAGE_SIZE = 3;
const PROJECTS_PAGE_SIZE = 1;

export const DataProvider = ({ children }) => {
    // Cached data (accumulated from all fetches)
    const [products, setProducts] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const [clients, setClients] = useState(null);
    const [projects, setProjects] = useState([]);

    // Pagination cursors (last doc for startAfter)
    const lastProductDoc = useRef(null);
    const lastBlogDoc = useRef(null);
    const lastProjectDoc = useRef(null);

    // Has more data flags
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [hasMoreBlogs, setHasMoreBlogs] = useState(true);
    const [hasMoreProjects, setHasMoreProjects] = useState(true);

    // Loading states
    const [productsLoading, setProductsLoading] = useState(false);
    const [blogsLoading, setBlogsLoading] = useState(false);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [projectsLoading, setProjectsLoading] = useState(false);

    // Refs to track pending promises (prevent duplicate requests)
    const productsFetchPromise = useRef(null);
    const blogsFetchPromise = useRef(null);
    const clientsFetchPromise = useRef(null);
    const projectsFetchPromise = useRef(null);

    // Fetch Products - First batch (10 products)
    const fetchProducts = useCallback(async () => {
        // If already have data, return it
        if (products.length > 0) return products;

        // If already loading, wait for the existing promise
        if (productsFetchPromise.current) {
            return productsFetchPromise.current;
        }

        setProductsLoading(true);
        productsFetchPromise.current = (async () => {
            try {
                const db = firebaseApp.firestore();
                const snapshot = await db.collection("product").orderBy("priority", "asc").limit(PRODUCTS_PAGE_SIZE).get();
                const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

                // Store last doc for pagination
                lastProductDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
                setHasMoreProducts(snapshot.docs.length === PRODUCTS_PAGE_SIZE);

                setProducts(data);
                return data;
            } catch (error) {
                console.error("Error fetching products:", error);
                return [];
            } finally {
                setProductsLoading(false);
                productsFetchPromise.current = null;
            }
        })();
        return productsFetchPromise.current;
    }, [products]);

    // Fetch ALL Products (bypassing pagination)
    const fetchAllProducts = useCallback(async () => {
        // If we already have products and NO more to load, we have everything.
        if (products.length > 0 && !hasMoreProducts) return products;

        if (productsFetchPromise.current) return productsFetchPromise.current;

        setProductsLoading(true);
        productsFetchPromise.current = (async () => {
            try {
                const db = firebaseApp.firestore();
                // Fetch ALL ordered by priority
                const snapshot = await db.collection("product").orderBy("priority", "asc").get();
                const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

                // Update pagination markers to signify "done"
                if (snapshot.docs.length > 0) {
                    lastProductDoc.current = snapshot.docs[snapshot.docs.length - 1];
                }
                setHasMoreProducts(false); // We have everything now

                setProducts(data);
                return data;
            } catch (error) {
                console.error("Error fetching all products:", error);
                return [];
            } finally {
                setProductsLoading(false);
                productsFetchPromise.current = null;
            }
        })();
        return productsFetchPromise.current;
    }, [products, hasMoreProducts]);

    // Fetch More Products (next batch)
    const fetchMoreProducts = useCallback(async () => {
        if (!hasMoreProducts || productsLoading || !lastProductDoc.current) return products;

        setProductsLoading(true);
        try {
            const db = firebaseApp.firestore();
            const snapshot = await db.collection("product")
                .orderBy("priority", "asc")
                .startAfter(lastProductDoc.current)
                .limit(PRODUCTS_PAGE_SIZE)
                .get();

            const newData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            // Update cursor
            lastProductDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
            setHasMoreProducts(snapshot.docs.length === PRODUCTS_PAGE_SIZE);

            const updatedProducts = [...products, ...newData];
            setProducts(updatedProducts);
            return updatedProducts;
        } catch (error) {
            console.error("Error fetching more products:", error);
            return products;
        } finally {
            setProductsLoading(false);
        }
    }, [hasMoreProducts, productsLoading, products]);

    // Fetch Blogs - First batch (3 blogs)
    const fetchBlogs = useCallback(async () => {
        if (blogs.length > 0) return blogs;

        if (blogsFetchPromise.current) {
            return blogsFetchPromise.current;
        }

        setBlogsLoading(true);
        blogsFetchPromise.current = (async () => {
            try {
                const db = firebaseApp.firestore();
                const snapshot = await db.collection("Blog")
                    .orderBy("priority", "asc")
                    .limit(BLOGS_PAGE_SIZE)
                    .get();
                const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

                lastBlogDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
                setHasMoreBlogs(snapshot.docs.length === BLOGS_PAGE_SIZE);

                setBlogs(data);
                return data;
            } catch (error) {
                console.error("Error fetching blogs:", error);
                return [];
            } finally {
                setBlogsLoading(false);
                blogsFetchPromise.current = null;
            }
        })();
        return blogsFetchPromise.current;
    }, [blogs]);

    // Fetch More Blogs (next batch)
    const fetchMoreBlogs = useCallback(async () => {
        if (!hasMoreBlogs || blogsLoading || !lastBlogDoc.current) return blogs;

        setBlogsLoading(true);
        try {
            const db = firebaseApp.firestore();
            const snapshot = await db.collection("Blog")
                .orderBy("priority", "asc")
                .startAfter(lastBlogDoc.current)
                .limit(BLOGS_PAGE_SIZE)
                .get();

            const newData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            lastBlogDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
            setHasMoreBlogs(snapshot.docs.length === BLOGS_PAGE_SIZE);

            const updatedBlogs = [...blogs, ...newData];
            setBlogs(updatedBlogs);
            return updatedBlogs;
        } catch (error) {
            console.error("Error fetching more blogs:", error);
            return blogs;
        } finally {
            setBlogsLoading(false);
        }
    }, [hasMoreBlogs, blogsLoading, blogs]);

    // Fetch Clients (no pagination - usually small list)
    const fetchClients = useCallback(async () => {
        if (clients) return clients;

        if (clientsFetchPromise.current) {
            return clientsFetchPromise.current;
        }

        setClientsLoading(true);
        clientsFetchPromise.current = (async () => {
            try {
                const db = firebaseApp.firestore();
                const snapshot = await db.collection("our_clients").get();
                const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                setClients(data);
                return data;
            } catch (error) {
                console.error("Error fetching clients:", error);
                return [];
            } finally {
                setClientsLoading(false);
                clientsFetchPromise.current = null;
            }
        })();
        return clientsFetchPromise.current;
    }, [clients]);

    // Fetch Projects - First batch (1 project)
    const fetchProjects = useCallback(async () => {
        if (projects.length > 0) return projects;

        if (projectsFetchPromise.current) {
            return projectsFetchPromise.current;
        }

        setProjectsLoading(true);
        projectsFetchPromise.current = (async () => {
            try {
                const db = firebaseApp.firestore();
                const snapshot = await db.collection("clientProjects")
                    .orderBy("order", "asc")
                    .limit(PROJECTS_PAGE_SIZE)
                    .get();

                const allProjects = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                // Filter for 9rnfc website
                const filtered = allProjects.filter(
                    (p) => p.website === "9rnfc" || p.website === "both" || !p.website
                );

                lastProjectDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
                setHasMoreProjects(snapshot.docs.length === PROJECTS_PAGE_SIZE);

                setProjects(filtered);
                return filtered;
            } catch (error) {
                console.error("Error fetching projects:", error);
                return [];
            } finally {
                setProjectsLoading(false);
                projectsFetchPromise.current = null;
            }
        })();
        return projectsFetchPromise.current;
    }, [projects]);

    // Fetch More Projects (next batch)
    const fetchMoreProjects = useCallback(async () => {
        if (!hasMoreProjects || projectsLoading || !lastProjectDoc.current) return projects;

        setProjectsLoading(true);
        try {
            const db = firebaseApp.firestore();
            const snapshot = await db.collection("clientProjects")
                .orderBy("order", "asc")
                .startAfter(lastProjectDoc.current)
                .limit(PROJECTS_PAGE_SIZE)
                .get();

            const allProjects = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            const filtered = allProjects.filter(
                (p) => p.website === "9rnfc" || p.website === "both" || !p.website
            );

            lastProjectDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
            setHasMoreProjects(snapshot.docs.length === PROJECTS_PAGE_SIZE);

            const updatedProjects = [...projects, ...filtered];
            setProjects(updatedProjects);
            return updatedProjects;
        } catch (error) {
            console.error("Error fetching more projects:", error);
            return projects;
        } finally {
            setProjectsLoading(false);
        }
    }, [hasMoreProjects, projectsLoading, projects]);

    // Get single product by ID (fetch all if needed for product details page)
    const getProductById = useCallback(async (id) => {
        // First check in cached products
        const cachedProduct = products.find((p) => p.id === id);
        if (cachedProduct) return cachedProduct;

        // If not found, fetch directly by ID
        try {
            const db = firebaseApp.firestore();
            const doc = await db.collection("product").doc(id).get();
            if (doc.exists) {
                return { id: doc.id, ...doc.data() };
            }
        } catch (error) {
            console.error("Error fetching product by ID:", error);
        }
        return null;
    }, [products]);

    // Get single blog by ID
    const getBlogById = useCallback(async (id) => {
        const cachedBlog = blogs.find((b) => b.id === id);
        if (cachedBlog) return cachedBlog;

        try {
            const db = firebaseApp.firestore();
            const doc = await db.collection("Blog").doc(id).get();
            if (doc.exists) {
                return { id: doc.id, ...doc.data() };
            }
        } catch (error) {
            console.error("Error fetching blog by ID:", error);
        }
        return null;
    }, [blogs]);

    const value = {
        // Data
        products,
        blogs,
        clients,
        projects,
        // Has more flags
        hasMoreProducts,
        hasMoreBlogs,
        hasMoreProjects,
        // Loading states
        productsLoading,
        blogsLoading,
        clientsLoading,
        projectsLoading,
        // Fetch functions (first batch)
        fetchProducts,
        fetchAllProducts,
        fetchBlogs,
        fetchClients,
        fetchProjects,
        // Fetch more functions (next batches)
        fetchMoreProducts,
        fetchMoreBlogs,
        fetchMoreProjects,
        // Get by ID functions
        getProductById,
        getBlogById,
    };

    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;
